import React, { type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { BlankSlate, Box, Text } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import { MaterializedFormSimpleFragment } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import { ElementPreview } from 'modules/forms/components/preview/ElementPreview'
import SidebarPreview from 'modules/forms/components/preview/SidebarPreview'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'

interface FormPreviewProps {
  form?: Form
  formId?: string
  resourceType?: string
  resourceId?: string // TODO NOT USED YED
  materializedForms?: MaterializedFormSimpleFragment[]
  emptyNavigateAction?: string
  sideContent?: ReactNode
  showElementName?: boolean
  titleSize?: 'default' | 'small'
}

const FormPreviewInline: React.FC<FormPreviewProps> = ({
  form,
  formId,
  resourceType,
  materializedForms,
  emptyNavigateAction,
  sideContent,
  showElementName = true,
  titleSize = 'default',
}) => {
  const { form: loadedForm, loadingForm } = useLoadForm({ formId, resourceType })
  form ||= loadedForm
  const [selectedElement, setSelectedElement] = useState<Element | null>(null)
  const navigate = useNavigate()

  // Deselect current selected element when form changed
  useEffect(() => {
    setSelectedElement(null)
  }, [form])

  if (loadingForm) {
    return <Loading expand />
  }

  if (!form) {
    return (
      <Box height="full" justifyContent="center" alignItems="center">
        <BlankSlate
          illustration="sign-post"
          title={i18n.t('forms.editor.not_found.title')}
          description={i18n.t('forms.editor.not_found.description')}
        />
      </Box>
    )
  }

  const isFormScorable = form.formType === 'knowledge' || form.configuration?.scorable || false

  return (
    <>
      <TwoColumnLayout
        mainColumnPosition="left"
        sideContent={
          sideContent || <SidebarPreview form={form} materializedForms={materializedForms} />
        }
      >
        {form.getElementCount() === 0 ? (
          <Box height="full" minHeight="s400" justifyContent="center" alignItems="center" gap="s24">
            <OneEmptyState
              {...(emptyNavigateAction
                ? {
                    actions: [
                      {
                        label: i18n.t('forms.modals.actions.navigate_to_edit'),
                        onClick: () => navigate(emptyNavigateAction),
                        type: 'default',
                        variant: 'outline',
                        icon: Pencil,
                      },
                    ],
                  }
                : {})}
              description={i18n.t('forms.editor.empty_state.description')}
              emoji="✏️"
              title={i18n.t('forms.editor.empty_state.title')}
            />
          </Box>
        ) : (
          <Box width="full" height="full" alignItems="center" padding="s16">
            <Box gap="s24" width="full" style={{ maxWidth: '720px' }}>
              {!selectedElement &&
                form.elements.map((element: Element, index) => (
                  <>
                    <ElementPreview
                      key={index}
                      element={element}
                      mode="preview"
                      showCorrectAnswerIcons={isFormScorable}
                      showElementName={showElementName}
                      titleSize={titleSize}
                    />
                    {element instanceof SectionElement && (
                      <>
                        {element.elements.map((child: Element, childIndex) => (
                          <ElementPreview
                            key={childIndex}
                            element={child}
                            mode="preview"
                            showCorrectAnswerIcons={isFormScorable}
                            showElementName={showElementName}
                            titleSize={titleSize}
                          />
                        ))}
                        <Box flexDirection="row" alignItems="center">
                          <Box
                            width="full"
                            className="border-solid border-0 border-t border-f1-border-secondary"
                          />
                          <Text.Normal whiteSpace="nowrap" className="text-base p-3">
                            {i18n.t('forms.section_end')}
                          </Text.Normal>
                          <Box
                            width="full"
                            className="border-solid border-0 border-t border-f1-border-secondary"
                          />
                        </Box>
                      </>
                    )}
                  </>
                ))}
              {selectedElement && (
                <ElementPreview
                  element={selectedElement}
                  mode="preview"
                  showCorrectAnswerIcons={isFormScorable}
                  showElementName={showElementName}
                  titleSize={titleSize}
                />
              )}
            </Box>
          </Box>
        )}
      </TwoColumnLayout>
    </>
  )
}

export default FormPreviewInline
